import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    OnDestroy,
    inject,
    ModelSignal,
    model,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import {
    ConfirmDialogComponent,
    ConfirmDialogData,
} from "../../../../../shared/_components/confirm-dialog/confirm-dialog.component";
import { TradingLockKind } from "../trading-lock/trading-lock.component";
import { UserFullInfo, UsersService } from "../users.service";
import { NotificationsService } from "../../../../../shared/_services/notifications.service";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-user-info-dialog",
    templateUrl: "./user-info-dialog.component.html",
    styleUrls: ["./user-info-dialog.component.scss"],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class UserInfoDialogComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject<any>();

    readonly data = inject<any>(MAT_DIALOG_DATA);
    readonly userId: ModelSignal<string | null> = model<string | null>(this.data.userId);
    readonly user: ModelSignal<UserFullInfo | null> = model<UserFullInfo | null>(null);
    readonly displayedColumns: string[] = ["packageName", "isMain", "expirationDate", "actions", "idx"];
    readonly botColumns: string[] = ["name", "id", "status", "tradingLock", "actions"];
    // As this dialog last saw each broker account's lock: the API has no read of a lock.
    readonly accountLockKinds = signal<ReadonlyMap<string, TradingLockKind>>(new Map<string, TradingLockKind>());

    constructor(
        private userService: UsersService,
        public dialogRef: MatDialogRef<UserInfoDialogComponent>,
        private notificationsService: NotificationsService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.userService
            .get(this.userId()!)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((user) => {
                this.user.set(user);
            });
    }

    ngOnDestroy(): void {
        // prevent memory leak when component destroyed
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    setAccountLockState(accountId: string, kind: TradingLockKind): void {
        this.accountLockKinds.update((kinds) => new Map(kinds).set(accountId, kind));
    }

    reloadBotData(botId: string): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: "Reload Bot Data",
                message: `Are you sure you want to reload data for bot ${botId}? This will delete all bot trading history and assign all orders from broker to this bot.`,
                type: "danger",
            } as ConfirmDialogData,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.userService
                .reloadBotData(this.userId()!, botId)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: () => {
                        this.notificationsService.showSuccess("Success", "Bot data reloaded successfully");
                    },
                    error: () => {
                        this.notificationsService.showError("Error", "Failed to reload data for bot. Try again");
                    },
                });
        });
    }

    runBot(botId: string): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: "Run Bot",
                message: `Are you sure you want to run bot ${botId} now? Market hours and usage limits are bypassed for admin runs.`,
                type: "warning",
            } as ConfirmDialogData,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.userService
                .runBot(this.userId()!, botId)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: (commandResult) => {
                        if (commandResult.success) {
                            this.notificationsService.showSuccess("Success", "Bot run started successfully");
                        } else {
                            this.notificationsService.showError(
                                "Error",
                                commandResult.error ?? "Failed to run bot. Try again",
                            );
                        }
                    },
                    error: () => {
                        this.notificationsService.showError("Error", "Failed to run bot. Try again");
                    },
                });
        });
    }
}
